import { create } from 'zustand';
import { RECENT_TAKE, recentService } from '../services/recentService';
import type { RecentRecord } from '../types/recent.types';

/**
 * The Recent list, cached per module (M4 · 86d44z223).
 *
 * Server-side data with a module-scoped cache, fetched when a panel opens. Deliberately NOT
 * persisted: the list lives on the server precisely so it follows the user between shared warehouse
 * terminals, and a local copy would reintroduce the leak that motivated going server-side.
 *
 * ⚠️ App-lifetime store + SPA sign-out means state SURVIVES a session end, so `reset` is wired into
 * `useLogoutSync`'s single choke point beside the notification reset. Without it the next person to
 * sign in on a shared terminal sees the previous user's record names until a refetch overwrites
 * them — the exact failure this feature was made server-side to avoid.
 */
interface RecentState {
  byModule: Record<string, RecentRecord[]>;
  loadingModule: string | null;
  fetchForModule: (moduleId: string) => Promise<void>;
  recordVisited: (row: RecentRecord) => void;
  reset: () => void;
}

export const useRecentStore = create<RecentState>()((set) => ({
  byModule: {},
  loadingModule: null,

  async fetchForModule(moduleId) {
    set({ loadingModule: moduleId });
    try {
      const rows = await recentService.getForModule(moduleId);
      set((s) => ({ byModule: { ...s.byModule, [moduleId]: rows }, loadingModule: null }));
    } catch {
      // Convenience data. A failure shows no Recent section rather than an error — and must never
      // block or decorate the panel the user opened.
      set((s) => ({ byModule: { ...s.byModule, [moduleId]: [] }, loadingModule: null }));
    }
  },

  /**
   * Splice a just-visited record into the module it belongs to, newest first.
   *
   * ⚠️ THE SERVER DECIDED `row.moduleId`, not this client. That invariant is the reason the visit
   * request carries no module, and it survives here — we are placing a row the server already
   * classified, not classifying one.
   *
   * ⛔ ONLY into a module already in the cache. Seeding one we have never fetched would render a
   * one-row Recent list that looks like the user's entire history, when the truth is simply that we
   * have not asked yet. An unfetched module is left alone and loads normally when its panel opens.
   */
  recordVisited(row) {
    set((s) => {
      const current = s.byModule[row.moduleId];
      if (!current) return s;

      // MOVE, don't append — mirroring the server's upsert. Re-opening one picklist ten times must
      // not fill the list with itself.
      const without = current.filter(
        (r) => !(r.entityType === row.entityType && r.entityId === row.entityId),
      );

      return {
        byModule: { ...s.byModule, [row.moduleId]: [row, ...without].slice(0, RECENT_TAKE) },
      };
    });
  },

  reset: () => set({ byModule: {}, loadingModule: null }),
}));
